import { useState, useEffect } from "react";
import { doc, setDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, FIREBASE_READY, STORAGE_READY } from "../../firebase";
import { useApp } from "../../contexts/AppContext";
import { sSave } from "../../utils/storage";
import { handleImageUpload } from "../../utils/imageUpload";

/** Convert a Firebase Storage error code to a plain English message */
function storageErrMsg(e) {
  const code = e?.code ?? "";
  if (code === "storage/unauthorized")
    return "Permission denied — make sure you are signed in as admin and the storage rules allow uploads.";
  if (code === "storage/unauthenticated")
    return "You must be signed in to upload photos.";
  if (code === "storage/quota-exceeded")
    return "Firebase Storage quota exceeded. Check your Firebase plan.";
  if (code === "storage/invalid-checksum" || code === "storage/retry-limit-exceeded")
    return "Network error during upload. Please check your connection and try again.";
  if (code === "storage/bucket-not-found")
    return "Storage bucket not found. The bucket 'lhda-website-media' may not be linked to this Firebase project.";
  if (code === "storage/object-not-found")
    return "Upload path not found. Check that Firebase Storage is fully initialised.";
  if (code === "storage/cannot-slice-blob")
    return "File read error — please try a different image file.";
  return `Upload failed (${code || e?.message || "unknown error"}).`;
}

export default function AboutTab({ flash, flashError, uploading, setUploading }) {
  const { about, setAbout } = useApp();

  const [local, setLocal] = useState(
    () => ({ ...about, officers: about.officers.map(o => ({ ...o })) })
  );

  /* Always sync local from Firestore whenever the context changes.
     After a save, setAbout(local) is called first so this becomes a no-op. */
  useEffect(() => {
    setLocal({ ...about, officers: about.officers.map(o => ({ ...o })) });
  }, [about]);

  const updOfficer = (i, f, v) =>
    setLocal(a => ({ ...a, officers: a.officers.map((o, j) => j === i ? { ...o, [f]: v } : o) }));
  const addOfficer = () =>
    setLocal(a => ({ ...a, officers: [...a.officers, { name: "", position: "", clan: "", photo: null }] }));
  const remOfficer = i =>
    setLocal(a => ({ ...a, officers: a.officers.filter((_, j) => j !== i) }));

  /* ── Officer photo upload ────────────────────────────────────────── */
  const handleOfficerImg = async (file, i) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      flashError("Please select an image file (JPEG, PNG, or WebP).");
      return;
    }

    if (FIREBASE_READY && STORAGE_READY) {
      if (file.size > 2 * 1024 * 1024) {
        flashError("Image is too large — please use a file under 2 MB.");
        return;
      }

      setUploading(true);
      try {
        /* 1 — Upload to Storage */
        const path = `officers/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
        const sr   = storageRef(storage, path);
        await uploadBytes(sr, file, { contentType: file.type });
        const url  = await getDownloadURL(sr);

        /* 2 — Build updated record and save to Firestore immediately */
        const updatedOfficers = local.officers.map((o, j) =>
          j === i ? { ...o, photo: url } : o
        );
        const updatedLocal = { ...local, officers: updatedOfficers };
        await setDoc(doc(db, "about", "main"), updatedLocal);

        /* 3 — Reflect in local and context state */
        setLocal(updatedLocal);
        setAbout(updatedLocal);
        flash("✓ Photo uploaded and saved.");
      } catch (e) {
        console.error("Officer photo upload error:", e);
        flashError(storageErrMsg(e));
      } finally {
        setUploading(false);
      }

    } else if (FIREBASE_READY && !STORAGE_READY) {
      flashError("Firebase Storage is not yet initialised. Go to the Firebase Console → Storage → Get Started.");

    } else {
      /* No Firebase — fall back to base64 data-URL stored in Firestore */
      handleImageUpload(file, img => updOfficer(i, "photo", img), 2);
    }
  };

  /* ── Save about + officers to Firestore ─────────────────────────── */
  const saveAbout = async () => {
    if (FIREBASE_READY) {
      try {
        await setDoc(doc(db, "about", "main"), local);
        setAbout(local);
      } catch (e) {
        console.error("Firestore save error:", e);
        flashError("Save failed (" + (e?.code || e?.message) + "). Check browser console.");
        return;
      }
    } else {
      setAbout(local);
      await sSave("lhda_about", local);
    }
    flash("✓ About content & officers saved!");
  };

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div>
      {/* About text */}
      <div className="acard">
        <div className="atitle">Edit About Content</div>
        <div className="field">
          <label>Mission Statement</label>
          <textarea value={local.mission}
            onChange={e => setLocal(a => ({ ...a, mission: e.target.value }))} rows={4} />
        </div>
        <div className="field">
          <label>Vision Statement</label>
          <textarea value={local.vision}
            onChange={e => setLocal(a => ({ ...a, vision: e.target.value }))} rows={4} />
        </div>
        <div className="field">
          <label>History / Background</label>
          <textarea value={local.history}
            onChange={e => setLocal(a => ({ ...a, history: e.target.value }))} rows={8} />
        </div>
        <button className="btn-g" onClick={saveAbout}>Save About Content</button>
      </div>

      {/* Officers */}
      <div className="acard">
        <div className="atitle">
          Executive Committee Officers
          <button className="btn-g"
            style={{ fontSize: ".78rem", padding: ".38rem .8rem" }}
            onClick={addOfficer}>
            + Add Officer
          </button>
        </div>

        <div className="info-banner">
          📸 Click the circle to upload a face photo — square, at least 300×300 px, under 2 MB (JPEG / PNG / WebP).
        </div>

        {local.officers.map((o, i) => (
          <div key={i} className="officer-admin-row">
            {/* Photo */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <label className="officer-photo-upload" style={{ cursor: uploading ? "wait" : "pointer" }}>
                <div className="officer-upload-ring">
                  {o.photo
                    ? <img src={o.photo} alt={o.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                    : <span style={{ fontSize: "1.6rem" }}>📷</span>
                  }
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: "none" }}
                  disabled={uploading}
                  onChange={e => { handleOfficerImg(e.target.files[0], i); e.target.value = ""; }}
                />
              </label>
              <div className="officer-upload-hint">
                {uploading ? "Uploading…" : o.photo ? "Click to change" : "Click to upload"}
              </div>
              {o.photo && !uploading && (
                <button onClick={() => updOfficer(i, "photo", null)}
                  style={{ marginTop: ".3rem", fontSize: ".65rem", color: "#e53935",
                           background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>
                  Remove
                </button>
              )}
            </div>

            {/* Fields */}
            <div className="officer-fields">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".6rem" }}>
                <div className="field" style={{ margin: 0 }}>
                  <label>Full Name</label>
                  <input value={o.name}
                    onChange={e => updOfficer(i, "name", e.target.value)} placeholder="Full name" />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label>Position</label>
                  <input value={o.position}
                    onChange={e => updOfficer(i, "position", e.target.value)} placeholder="Position / Title" />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: ".6rem", alignItems: "end" }}>
                <div className="field" style={{ margin: 0 }}>
                  <label>Clan</label>
                  <input value={o.clan}
                    onChange={e => updOfficer(i, "clan", e.target.value)} placeholder="Clan or —" />
                </div>
                <button className="btn-r" onClick={() => remOfficer(i)}>✕ Remove</button>
              </div>
            </div>
          </div>
        ))}

        <button className="btn-g" style={{ marginTop: ".75rem" }} onClick={saveAbout}
          disabled={uploading}>
          Save Officers &amp; Photos
        </button>
      </div>
    </div>
  );
}
